import { type SortingState } from '@tanstack/react-table'
import { useEffect, useState } from 'react'

interface UsePersistentSortingOptions<T = SortingState> {
  key: string
  initialSorting?: T
}

export function usePersistentSorting<T = SortingState>({
  key,
  initialSorting = [] as T,
}: UsePersistentSortingOptions<T>) {
  const [sorting, setSorting] = useState<T>(() => {
    // Try to load from localStorage on initialization
    try {
      const stored = localStorage.getItem(key)
      if (stored) {
        const parsed = JSON.parse(stored)
        return Array.isArray(parsed) ? (parsed as T) : initialSorting
      }
    } catch (error) {
      console.warn('Failed to load sorting state from localStorage:', error)
    }
    return initialSorting
  })

  // Save to localStorage whenever sorting changes
  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(sorting))
    } catch (error) {
      console.warn('Failed to save sorting state to localStorage:', error)
    }
  }, [key, sorting])

  return [sorting, setSorting] as const
}

// Utility function to clear persistent sorting state
export function clearPersistentSorting(key: string) {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.warn('Failed to clear sorting state from localStorage:', error)
  }
}
